import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TicketTabType } from '../enums/ticket-tab-type.enum';
import { TicketRightPanelService } from './ticket-right-panel.service';
import { NoteService } from './note.service';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';

@Injectable({ providedIn: 'root' })
export class TicketDetailsPanelService {
    clickTime;
    currentTicket;
    currentTicketId: number;
    isTicketDetailsOpened;
    needToOpenNotesTab;
    replyIdToRemove: number;

    private switchTicketsTimer: ReturnType<typeof setTimeout>;
    private readonly ticketSelectionQueryParams = {
        ticketId: null,
        noteId: null,
        replyId: null,
        action: null,
        nextTicketAfterClose: null,
    };

    constructor(
        private activatedRoute: ActivatedRoute,
        private commonDataService: CommonDataService,
        private router: Router,
        private ticketRightPanelService: TicketRightPanelService,
        private noteService: NoteService,
    ) {}

    open(ticketId: number, needToReplaceHistory = false, needToCleanNoteId?, isSingleTicket = false): void {
        if (this.currentTicketId === ticketId) {
            return;
        }

        clearTimeout(this.switchTicketsTimer);
        this.isTicketDetailsOpened = false;

        this.switchTicketsTimer = setTimeout(() => {
            this.currentTicketId = ticketId;
            this.needToOpenNotesTab = false;

            const newQueryParams = { ticketId };
            if (needToCleanNoteId) {
                newQueryParams['noteId'] = null;
            }

            newQueryParams['emptySelection'] = null;
            newQueryParams['nextTicketAfterClose'] = null;

            if (this.replyIdToRemove) {
                this.replyIdToRemove = null;
                newQueryParams['replyId'] = null;
            }

            if (isSingleTicket) {
                this.router
                    .navigate([`/${this.commonDataService.communityName}`, 'ticket'], {
                        queryParams: { ticketId },
                    })
                    .then(() => {
                        this.isTicketDetailsOpened = true;
                    });
            } else {
                this.router
                    .navigate([], {
                        relativeTo: this.activatedRoute,
                        queryParams: newQueryParams,
                        queryParamsHandling: 'merge',
                        replaceUrl: needToReplaceHistory,
                    })
                    .then(() => {
                        this.isTicketDetailsOpened = true;
                    });
            }
        }, 10);
    }

    openTicketNote(ticketId, noteId?) {
        if (this.currentTicketId === ticketId) {
            if (this.ticketRightPanelService.isRightPanelOpened) {
                this.ticketRightPanelService.onActivateTabSubject.next(TicketTabType.notes);

                if (noteId) {
                    this.noteService.scrollToView();
                }
            } else {
                this.ticketRightPanelService.open({
                    ticket: this.currentTicket,
                    tabType: TicketTabType.notes,
                });
            }

            this.needToOpenNotesTab = true;

            this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: { ticketId, noteId, emptySelection: null, nextTicketAfterClose: null },
                queryParamsHandling: 'merge',
            });
        } else {
            this.isTicketDetailsOpened = false;

            setTimeout(() => {
                this.currentTicketId = ticketId;
                this.needToOpenNotesTab = true;

                this.router
                    .navigate([], {
                        relativeTo: this.activatedRoute,
                        queryParams: { ticketId, noteId, emptySelection: null, nextTicketAfterClose: null },
                        queryParamsHandling: 'merge',
                    })
                    .then(() => {
                        this.isTicketDetailsOpened = true;
                    });
            }, 50);
        }
    }

    close(ticketId?: number, clearRoute = false): void {
        // A destroyed ticket view must not cancel opening the next ticket.
        if (!ticketId || clearRoute) {
            clearTimeout(this.switchTicketsTimer);
        }
        this.resetCurrentSelection(ticketId);

        if (clearRoute) {
            this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: {
                    ...this.ticketSelectionQueryParams,
                    emptySelection: null,
                },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        }
    }

    clearRouteSelection(ticketId?: number): void {
        this.close(ticketId, true);
    }

    setExplicitEmptySelection(
        ticketId?: number,
        automaticallyOpenNext = false,
        closedTicketIds?: number[],
    ): Promise<boolean> {
        clearTimeout(this.switchTicketsTimer);
        this.resetCurrentSelection(ticketId);

        return this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {
                ...this.ticketSelectionQueryParams,
                emptySelection: 1,
                nextTicketAfterClose: automaticallyOpenNext ? (closedTicketIds?.join(',') ?? ticketId) : null,
            },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    private resetCurrentSelection(ticketId?: number): void {
        if (!ticketId || ticketId === this.currentTicketId) {
            this.currentTicketId = null;
            this.isTicketDetailsOpened = false;
        }
    }
}
